/**
 * lib/net/reachability.ts — ¿el SERVIDOR contesta?
 *
 * `navigator.onLine` responde otra pregunta: si el equipo tiene una interfaz de red
 * levantada. Con wifi conectado a un módem sin internet, con un portal cautivo, o con el
 * servidor apagado, el navegador sigue diciendo "en línea" y la aplicación se comportaba
 * como si el servidor estuviera ahí: intentaba cobrar contra un servidor inalcanzable en
 * vez de capturar la venta sin conexión, y el guardián de entorno bloqueaba el POS entero
 * por "identidad no verificada".
 *
 * Aquí vive el único dato que sí responde la pregunta correcta: la última sonda de
 * identidad, que es una llamada real al servidor. Quien la ejecuta (`probeRuntime`) marca
 * el resultado; el resto de la aplicación lo lee.
 *
 * Dos banderas, distintas a propósito:
 *
 *   · alcanzable      — el servidor contestó algo. Decide si se opera en línea o sin conexión.
 *   · identidadVerificada — además, contestó QUIEN dice ser y el guardián no bloquea.
 *     Es la que exige el sincronizador antes de mandar nada: estar sin conexión no autoriza
 *     a vaciar la cola contra el primer servidor que responda.
 */

let alcanzable = typeof navigator === 'undefined' ? true : navigator.onLine
let identidadVerificada = false
const suscriptores = new Set<() => void>()

/** Cada cuánto se vuelve a preguntar por el servidor mientras no contesta. */
const REINTENTO_MS = 15_000
let volverAPreguntar: (() => void) | null = null
let vigilancia: ReturnType<typeof setInterval> | null = null

/**
 * Quien sepa preguntar por el servidor se registra aquí (lo hace el proveedor de entorno
 * con su propia sonda). Mientras el servidor no conteste, se le pide reintentar; en cuanto
 * contesta, la vigilancia se apaga sola. Da igual quién marcó la caída.
 */
export function registrarSondaDeReconexion(fn: () => void): void {
  volverAPreguntar = fn
  if (!alcanzable) vigilar()
}

function vigilar(): void {
  if (vigilancia || !volverAPreguntar) return
  vigilancia = setInterval(() => volverAPreguntar?.(), REINTENTO_MS)
}

function dejarDeVigilar(): void {
  if (!vigilancia) return
  clearInterval(vigilancia)
  vigilancia = null
}

const avisar = () => {
  for (const f of suscriptores) f()
}

export function servidorAlcanzable(): boolean {
  return alcanzable
}

export function marcarServidorAlcanzable(valor: boolean): void {
  if (alcanzable === valor) return
  alcanzable = valor
  if (valor) dejarDeVigilar()
  else vigilar()
  avisar()
}

export function identidadDelServidorVerificada(): boolean {
  return identidadVerificada
}

export function marcarIdentidadVerificada(valor: boolean): void {
  if (identidadVerificada === valor) return
  identidadVerificada = valor
  avisar()
}

/** Notifica cualquier cambio de alcance o de identidad. Devuelve la baja. */
export function suscribirAlcance(cb: () => void): () => void {
  suscriptores.add(cb)
  return () => suscriptores.delete(cb)
}

if (typeof window !== 'undefined') {
  // Perder la interfaz de red SÍ prueba que no hay servidor.
  window.addEventListener('offline', () => {
    marcarServidorAlcanzable(false)
    marcarIdentidadVerificada(false)
  })
  // Recuperarla NO prueba lo contrario: sólo la sonda, que es una llamada real, puede
  // volver a marcar alcanzable. Por eso aquí no se marca `true`.
}
